import { useState, useEffect, useRef, useCallback } from 'react';
import {
  fetchRemitBatch, createRemitBatch, updateRemitBatch, finalizeRemitBatch,
  overrideRemitBatch, fetchPayers, fetchPatientsByPayer, fetchPatientClaimsForRemit,
} from '../services/api';
import OverrideBanner from './OverrideBanner';
import { useCanMoveMoney, MONEY_ROLE_HINT } from '../contexts/AuthContext';
import './BatchRemitModal.css';
import { TYPED_PR, typedPrFormValues, typedPrTotal, typedPrPayload } from '../utils/typedPatientResp';

const PAYER_ORDER_LABELS = { 1: 'Primary', 2: 'Secondary', 3: 'Tertiary' };

const fmt = (n) =>
  '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const toFloat = (v) => parseFloat(v) || 0;

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ChevronIcon({ open }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.18s' }}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4h6v2" />
    </svg>
  );
}

// ─── Payer combobox ──────────────────────────────────────────
// Loads all payers upfront on first focus, then filters client-side.
// When a payer is selected, shows a "Change" button instead of the input.
function PayerCombobox({ value, payerId, onChange, disabled }) {
  const [payersList, setPayersList]   = useState([]);
  const [payerSearch, setPayerSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputRef = useRef(null);
  const loaded   = useRef(false);

  const loadPayers = useCallback(async () => {
    if (loaded.current) return;
    loaded.current = true;
    try {
      const res = await fetchPayers();
      setPayersList((res.data ?? res ?? []).filter((p) => !p.is_self_pay_payer));
    } catch { loaded.current = false; }
  }, []);

  const filtered = payersList.filter((p) =>
    !payerSearch.trim() ||
    p.name.toLowerCase().includes(payerSearch.toLowerCase()) ||
    (p.payer_id_number && p.payer_id_number.toLowerCase().includes(payerSearch.toLowerCase()))
  );

  const pick = (payer) => {
    onChange({ payer_id: payer.payer_id, payer_name: payer.name });
    setDropdownOpen(false);
    setPayerSearch('');
  };

  const handleChange = () => {
    onChange({ payer_id: null, payer_name: '' });
    setPayerSearch('');
    setDropdownOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  if (payerId) {
    return (
      <div className="brm-payer-selected">
        <span className="brm-payer-selected-name">{value}</span>
        {!disabled && (
          <button className="brm-payer-change-btn" type="button" onClick={handleChange}>
            Change
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="brm-payer-combobox">
      <input
        ref={inputRef}
        type="text"
        className="brm-input"
        placeholder="Search payer…"
        value={payerSearch}
        disabled={disabled}
        autoComplete="off"
        onFocus={() => { loadPayers(); setDropdownOpen(true); }}
        onChange={(e) => { setPayerSearch(e.target.value); setDropdownOpen(true); }}
        onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
      />
      {dropdownOpen && (
        <ul className="brm-payer-dropdown">
          {payersList.length === 0 ? (
            <li className="brm-payer-dropdown-loading">Loading payers…</li>
          ) : filtered.length === 0 ? (
            <li className="brm-payer-dropdown-empty">No payers match &quot;{payerSearch}&quot;</li>
          ) : (
            filtered.slice(0, 12).map((p) => (
              <li key={p.payer_id} className="brm-payer-dropdown-item" onMouseDown={() => pick(p)}>
                <span className="brm-typeahead-name">{p.name}</span>
                {p.payer_id_number && <span className="brm-typeahead-id">{p.payer_id_number}</span>}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

// ─── Patient search ──────────────────────────────────────────
// When a payer is selected, pre-loads all patients for that payer and
// filters them client-side as the user types. Clicking a patient loads
// their claims for the current payer.
function PatientSearch({ payerId, onAddClaim, existingClaimIds, disabled }) {
  const [query, setQuery]               = useState('');
  const [payerPatients, setPayerPatients] = useState([]);
  const [loadingPP, setLoadingPP]         = useState(false);
  const [selected, setSelected]           = useState(null);
  const [claims, setClaims]               = useState([]);
  const [loadingClaims, setLoadingClaims] = useState(false);

  // Reload patient list whenever payer changes
  useEffect(() => {
    setSelected(null);
    setClaims([]);
    setQuery('');
    if (!payerId) { setPayerPatients([]); return; }
    setLoadingPP(true);
    fetchPatientsByPayer(payerId)
      .then((res) => setPayerPatients(res.data || []))
      .catch(() => setPayerPatients([]))
      .finally(() => setLoadingPP(false));
  }, [payerId]);

  const filteredPatients = payerPatients.filter((p) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      `${p.first_name} ${p.last_name}`.toLowerCase().includes(q) ||
      (p.emr_patient_id && p.emr_patient_id.toLowerCase().includes(q))
    );
  });

  const pickPatient = async (patient) => {
    setSelected(patient);
    setQuery(`${patient.first_name} ${patient.last_name}`);
    setLoadingClaims(true);
    try {
      const res = await fetchPatientClaimsForRemit(patient.patient_id, payerId);
      setClaims(res.data || []);
    } catch { setClaims([]); }
    finally { setLoadingClaims(false); }
  };

  const clear = () => {
    setQuery('');
    setSelected(null);
    setClaims([]);
  };

  return (
    <div className="brm-patient-search">
      <div className="brm-patient-search-row">
        <div className="brm-patient-input-wrap">
          <svg className="brm-patient-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="brm-input brm-patient-input"
            type="text"
            placeholder={payerId ? 'Filter patients by name or MRN…' : 'Select a payer first'}
            value={query}
            disabled={disabled || !payerId}
            onChange={(e) => { setQuery(e.target.value); if (selected) { setSelected(null); setClaims([]); } }}
          />
          {query && (
            <button className="brm-patient-clear" onClick={clear} tabIndex={-1}>
              <CloseIcon />
            </button>
          )}
        </div>
        {loadingPP && <div className="brm-mini-spinner" />}
      </div>

      {/* Patient list — shown when payer selected and no patient picked */}
      {payerId && !selected && (
        loadingPP ? null : filteredPatients.length === 0 ? (
          <p className="brm-patient-none">
            {query.trim()
              ? `No patients match "${query}"`
              : 'No patients found for this payer.'}
          </p>
        ) : (
          <ul className="brm-patient-list">
            {filteredPatients.map((p) => (
              <li key={p.patient_id} className="brm-patient-item" onClick={() => pickPatient(p)}>
                <div className="brm-patient-name-row">
                  <span className="brm-patient-name">{p.first_name} {p.last_name}</span>
                  {p.facility_name && <span className="brm-claim-card-facility">{p.facility_name}</span>}
                </div>
                <span className="brm-patient-meta">
                  {p.emr_patient_id ? `MRN: ${p.emr_patient_id}` : ''}
                  {p.date_of_birth ? ` · ${new Date(p.date_of_birth).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )
      )}

      {/* Claims for selected patient */}
      {selected && (
        <div className="brm-claims-panel">
          <div className="brm-claims-panel-header">
            <span className="brm-claims-panel-name">{selected.first_name} {selected.last_name}</span>
            {selected.facility_name && <span className="brm-claims-panel-facility">{selected.facility_name}</span>}
            <button className="brm-patient-back-btn" onClick={clear}>← Back</button>
          </div>
          {loadingClaims ? (
            <div className="brm-claims-loading"><div className="brm-mini-spinner" /> Loading claims…</div>
          ) : claims.length === 0 ? (
            <p className="brm-claims-empty">No claims found for this patient with the selected payer.</p>
          ) : (
            <table className="brm-claims-table">
              <thead>
                <tr>
                  <th>Claim #</th>
                  <th>DOS</th>
                  <th>Facility</th>
                  <th>Payer Role</th>
                  <th>Billed</th>
                  <th>Remit</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {claims.map((c) => {
                  const alreadyAdded = existingClaimIds.includes(c.claim_id);
                  const hasRemit     = !!c.remit_date;
                  return (
                    <tr key={c.claim_id} className={alreadyAdded ? 'brm-claim-row-added' : ''}>
                      <td className="brm-claim-num">{c.claim_number}</td>
                      <td>{c.service_date ? new Date(c.service_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</td>
                      <td>{c.facility_name}</td>
                      <td>
                        <span className={`brm-payer-order-badge brm-po-${c.payer_order}`}>
                          {PAYER_ORDER_LABELS[c.payer_order] || `Order ${c.payer_order}`}
                        </span>
                      </td>
                      <td>{fmt(c.billed_amount)}</td>
                      <td>
                        {hasRemit ? (
                          <span className="brm-has-remit">Posted {fmt(c.remit_paid_amount)}</span>
                        ) : (
                          <span className="brm-no-remit">Pending</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="brm-add-claim-btn"
                          disabled={alreadyAdded || disabled}
                          onClick={() => {
                            const forwardedBalance =
                              c.payer_order > 1 && c.prev_allowed_amount != null
                                ? (c.prev_patient_resp || 0) > 0
                                  ? c.prev_patient_resp
                                  : Math.max(0, c.prev_allowed_amount - (c.prev_paid_amount || 0))
                                : null;
                            onAddClaim({
                              claim_id:               c.claim_id,
                              claim_number:           c.claim_number,
                              patient_name:           `${selected.first_name} ${selected.last_name}`,
                              facility_name:          c.facility_name,
                              payer_order:            c.payer_order,
                              billed_amount:          forwardedBalance ?? c.billed_amount,
                              allowed_amount:         '',
                              paid_amount:            '',
                              contractual_adj:        '',
                              other_adj:              '',
                              patient_responsibility: '',
                              pr_deductible: '', pr_coinsurance: '', pr_copay: '', pr_other: '',
                              adjustment_codes:       '',
                              denial_reason:          '',
                              notes:                  '',
                              prev_remit: c.prev_payer_name ? {
                                payer_name:        c.prev_payer_name,
                                billed_amount:     c.prev_billed_amount,
                                allowed_amount:    c.prev_allowed_amount,
                                paid_amount:       c.prev_paid_amount,
                                contractual_adj:   c.prev_contractual_adj,
                                other_adj:         c.prev_other_adj,
                                patient_resp:      c.prev_patient_resp,
                                forwarded_balance: forwardedBalance,
                              } : null,
                            });
                          }}
                        >
                          {alreadyAdded ? 'Added' : '+ Add'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Individual claim row ─────────────────────────────────────
// `readonly` gates structural actions (the remove button); `fieldsDisabled`
// gates the value inputs. They diverge during an override: the claim set is
// locked (no remove) but the amounts unlock for correction.
function ClaimRow({ row, onChange, onRemove, readonly, fieldsDisabled = readonly }) {
  const [open, setOpen] = useState(true);

  const set = (field) => (e) => onChange(row.claim_id, field, e.target.value);

  // Auto-calc contractual adj = billed − allowed − other adj. The billed − allowed gap is
  // contractual PLUS any OA/PI amount (Step 13), so a hand-keyed other_adj is taken out of
  // it rather than counted twice, and is never overwritten here.
  useEffect(() => {
    const billed  = toFloat(row.billed_amount);
    const allowed = toFloat(row.allowed_amount);
    if (allowed > 0) {
      onChange(row.claim_id, 'contractual_adj', Math.max(0, billed - allowed - toFloat(row.other_adj)).toFixed(2));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.allowed_amount, row.billed_amount, row.other_adj]);

  // Typed PR (Step 13, S13-w): once any part is keyed, the total is their sum.
  const typedPr = typedPrTotal(row);
  const patientResp = typedPr.typed ? typedPr.total.toFixed(2) : row.patient_responsibility;

  const balanceFwd = toFloat(row.allowed_amount) > 0
    ? toFloat(patientResp) > 0
      ? toFloat(patientResp)
      : Math.max(0, toFloat(row.allowed_amount) - toFloat(row.paid_amount))
    : null;

  return (
    <div className={`brm-claim-card ${open ? 'open' : ''}`}>
      <div className="brm-claim-card-header" onClick={() => setOpen(!open)}>
        <div className="brm-claim-card-left">
          <span className={`brm-payer-order-badge brm-po-${row.payer_order}`}>
            {PAYER_ORDER_LABELS[row.payer_order]}
          </span>
          <span className="brm-claim-card-patient">{row.patient_name}</span>
          <span className="brm-claim-card-sep">—</span>
          <span className="brm-claim-card-claim">Claim #{row.claim_number}</span>
          {row.facility_name && (
            <span className="brm-claim-card-facility">{row.facility_name}</span>
          )}
        </div>
        <div className="brm-claim-card-right">
          {!readonly && (
            <button
              className="brm-claim-remove"
              onClick={(e) => { e.stopPropagation(); onRemove(row.claim_id); }}
              title="Remove claim"
            >
              <TrashIcon />
            </button>
          )}
          <ChevronIcon open={open} />
        </div>
      </div>

      {open && (
        <div className="brm-claim-card-body">
          {row.prev_remit && (
            <div className="brm-prev-remit">
              <div className="brm-prev-remit-header">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                Prior payer: <strong>{row.prev_remit.payer_name}</strong>
              </div>
              <div className="brm-prev-remit-grid">
                <div className="brm-prev-remit-item">
                  <span>Billed</span>
                  <span>{fmt(row.prev_remit.billed_amount)}</span>
                </div>
                <div className="brm-prev-remit-item">
                  <span>Allowed</span>
                  <span>{fmt(row.prev_remit.allowed_amount)}</span>
                </div>
                <div className="brm-prev-remit-item">
                  <span>Contractual Adj.</span>
                  <span>{fmt(row.prev_remit.contractual_adj)}</span>
                </div>
                {toFloat(row.prev_remit.other_adj) > 0 && (
                  <div className="brm-prev-remit-item">
                    <span>Other Adj. (OA/PI)</span>
                    <span>{fmt(row.prev_remit.other_adj)}</span>
                  </div>
                )}
                <div className="brm-prev-remit-item brm-prev-remit-paid-item">
                  <span>Paid</span>
                  <span>{fmt(row.prev_remit.paid_amount)}</span>
                </div>
                <div className="brm-prev-remit-item brm-prev-remit-fwd">
                  <span>Forwarded Balance</span>
                  <strong>{fmt(row.prev_remit.forwarded_balance)}</strong>
                </div>
              </div>
            </div>
          )}
          <div className="brm-grid-3">
            <label className="brm-field">
              <span>Billed Amount</span>
              <input type="number" step="0.01" min="0" className="brm-input" value={row.billed_amount} onChange={set('billed_amount')} placeholder="0.00" disabled={fieldsDisabled} />
            </label>
            <label className="brm-field">
              <span>Allowed Amount</span>
              <input type="number" step="0.01" min="0" className="brm-input" value={row.allowed_amount} onChange={set('allowed_amount')} placeholder="0.00" disabled={fieldsDisabled} />
            </label>
            <label className="brm-field">
              <span>Paid Amount</span>
              <input type="number" step="0.01" min="0" className="brm-input" value={row.paid_amount} onChange={set('paid_amount')} placeholder="0.00" disabled={fieldsDisabled} />
            </label>
            <label className="brm-field">
              <span>Contractual Adj. (CO-45)</span>
              <input type="number" step="0.01" min="0" className="brm-input" value={row.contractual_adj} onChange={set('contractual_adj')} placeholder="0.00" disabled={fieldsDisabled} />
            </label>
            <label className="brm-field">
              <span>Other Adj. (OA/PI)</span>
              <input type="number" step="0.01" min="0" className="brm-input" value={row.other_adj ?? ''} onChange={set('other_adj')} placeholder="0.00" disabled={fieldsDisabled} />
            </label>
            <label className="brm-field">
              <span>Patient Responsibility</span>
              <input type="number" step="0.01" min="0" className="brm-input" value={patientResp} onChange={set('patient_responsibility')} placeholder="0.00" disabled={fieldsDisabled} readOnly={typedPr.typed} />
            </label>
            {TYPED_PR.map(({ field, label }) => (
              <label className="brm-field" key={field}>
                <span>{label}</span>
                <input type="number" step="0.01" min="0" className="brm-input" value={row[field] ?? ''} onChange={set(field)} placeholder="0.00" disabled={fieldsDisabled} />
              </label>
            ))}
          </div>
          {balanceFwd !== null && balanceFwd > 0 && (
            <div className="brm-balance-preview">
              <span>Balance forwarded to next payer</span>
              <strong>{fmt(balanceFwd)}</strong>
            </div>
          )}
          <div className="brm-grid-2" style={{ marginTop: 12 }}>
            <label className="brm-field">
              <span>Adj. Reason Codes (CARC/RARC)</span>
              <input type="text" className="brm-input" value={row.adjustment_codes} onChange={set('adjustment_codes')} placeholder="e.g. CO-45, PR-1" disabled={fieldsDisabled} />
            </label>
            <label className="brm-field">
              <span>Denial Reason</span>
              <input type="text" className="brm-input" value={row.denial_reason} onChange={set('denial_reason')} placeholder="Reason if denied" disabled={fieldsDisabled} />
            </label>
          </div>
          <label className="brm-field" style={{ marginTop: 12 }}>
            <span>Notes</span>
            <textarea className="brm-input brm-textarea" rows={2} value={row.notes} onChange={set('notes')} placeholder="Optional notes…" disabled={fieldsDisabled} />
          </label>
        </div>
      )}
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────
export default function BatchRemitModal({ batchId, readonly, initialData, onClose, onSaved }) {
  const canMove = useCanMoveMoney();
  const [step, setStep]           = useState(batchId || initialData ? 'entry' : 'mode');
  const [mode, setMode]           = useState(initialData?.mode || 'batch');
  const [header, setHeader]       = useState({
    payer_id:       initialData?.payerId    || null,
    payer_name:     initialData?.payerName  || '',
    check_number:   '',
    check_amount:   '',
    remit_date:     '',
    submitted_date: initialData?.submittedAt?.slice(0, 10) || '',
  });
  const [claimRows, setClaimRows] = useState([]);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [loadingBatch, setLoadingBatch] = useState(!!batchId);
  const [status, setStatus]       = useState(null);          // loaded batch status
  const [overrides, setOverrides] = useState([]);            // prior override events
  const [overriding, setOverriding] = useState(false);       // in override (unlock) mode
  const [reason, setReason]       = useState('');            // override reason

  // A finalized batch is normally read-only; override mode unlocks its value fields.
  const fieldsDisabled = overriding ? false : readonly;

  // Pre-add claim from COBTimeline deep-link
  useEffect(() => {
    if (initialData?.claimId && initialData?.payerOrder && initialData?.payerName) {
      setClaimRows([{
        claim_id:               initialData.claimId,
        claim_number:           initialData.claimNumber || `#${initialData.claimId}`,
        patient_name:           initialData.patientName || '',
        facility_name:          initialData.facilityName || '',
        payer_order:            initialData.payerOrder,
        billed_amount:          initialData.billedAmount ?? '',
        allowed_amount:         '',
        paid_amount:            '',
        contractual_adj:        '',
        other_adj:              '',
        patient_responsibility: '',
        pr_deductible: '', pr_coinsurance: '', pr_copay: '', pr_other: '',
        adjustment_codes:       '',
        denial_reason:          '',
        notes:                  '',
        prev_remit:             initialData.prevRemit ?? null,
      }]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load existing batch for resume / view
  useEffect(() => {
    if (!batchId) return;
    (async () => {
      setLoadingBatch(true);
      try {
        const res = await fetchRemitBatch(batchId);
        const b   = res.data;
        setStatus(b.status || null);
        setOverrides(b.overrides || []);
        setHeader({
          payer_id:       b.payer_id       || null,
          payer_name:     b.payer_name     || '',
          check_number:   b.check_number   || '',
          check_amount:   String(b.check_amount ?? ''),
          remit_date:     b.remit_date?.slice(0, 10) || '',
          submitted_date: '',
        });
        setClaimRows(
          (b.claim_remits || []).map((cr) => ({
            claim_id:               cr.claim_id,
            claim_number:           cr.claim_number,
            patient_name:           cr.patient_name,
            facility_name:          cr.facility_name,
            payer_order:            cr.payer_order,
            billed_amount:          String(cr.billed_amount ?? ''),
            allowed_amount:         String(cr.allowed_amount ?? ''),
            paid_amount:            String(cr.paid_amount ?? ''),
            contractual_adj:        String(cr.contractual_adj ?? ''),
            other_adj:              String(cr.other_adj ?? ''),
            patient_responsibility: String(cr.patient_responsibility ?? ''),
            ...typedPrFormValues(cr),
            adjustment_codes:       cr.adjustment_codes || '',
            denial_reason:          cr.denial_reason    || '',
            notes:                  cr.notes            || '',
          }))
        );
        setStep('entry');
      } catch { setError('Failed to load remit batch.'); }
      finally { setLoadingBatch(false); }
    })();
  }, [batchId]);

  const setHead = (field) => (e) => setHeader((h) => ({ ...h, [field]: e.target.value }));

  const updateRow = (claimId, field, value) => {
    setClaimRows((rows) => rows.map((r) => r.claim_id === claimId ? { ...r, [field]: value } : r));
  };

  const removeRow = (claimId) => {
    setClaimRows((rows) => rows.filter((r) => r.claim_id !== claimId));
  };

  const addClaim = (row) => {
    setClaimRows((rows) => [...rows, row]);
  };

  // Validation
  const totalPaid   = claimRows.reduce((s, r) => s + toFloat(r.paid_amount), 0);
  const checkAmount = toFloat(header.check_amount);
  // A $0 check (zero-pay EOB from secondary) is valid when all claim paid amounts are also $0.
  const isBalanced  = claimRows.length > 0 && (
    (checkAmount === 0 && totalPaid === 0) ||
    (checkAmount > 0  && Math.abs(totalPaid - checkAmount) <= 0.01)
  );
  const deficit     = checkAmount - totalPaid;

  // Denial awareness: a denied claim carries a denial_reason. A $0 check made up
  // of denials must read as "Denied", not a generic zero-pay EOB. (A zero-pay
  // applied to the patient's deductible is NOT a denial and has no reason.)
  const denialRows    = claimRows.filter((r) => (r.denial_reason || '').trim());
  const denialReasons = [...new Set(denialRows.map((r) => r.denial_reason.trim()))];
  const hasDenials    = denialRows.length > 0;
  const allDenied     = claimRows.length > 0 && denialRows.length === claimRows.length;
  // Avoid "Denied: Denied …" when the reason text already says it.
  const denyText      = (reason) => (/deni/i.test(reason) ? reason : `Denied: ${reason}`);
  const zeroPayLabel  = denialRows.length === 0
    ? 'Zero-pay EOB — ready to finalize'
    : allDenied
      ? `${denialReasons.length === 1 ? denyText(denialReasons[0]) : `Denied — ${denialRows.length} claims`} — ready to finalize`
      : `Zero-pay — ${denialRows.length} denied — ready to finalize`;

  const buildPayload = (status) => ({
    payer_id:       header.payer_id,
    payer_name:     header.payer_name.trim(),
    check_number:   header.check_number.trim(),
    check_amount:   toFloat(header.check_amount),
    remit_date:     header.remit_date,
    submitted_date: header.submitted_date || null,
    status,
    claims: claimRows.map((r) => ({
      claim_id:               r.claim_id,
      payer_order:            r.payer_order,
      billed_amount:          toFloat(r.billed_amount),
      allowed_amount:         toFloat(r.allowed_amount),
      paid_amount:            toFloat(r.paid_amount),
      contractual_adj:        toFloat(r.contractual_adj),
      other_adj:              toFloat(r.other_adj),
      // If the biller left patient_responsibility blank, derive it from allowed - paid
      // so the database always has an accurate "what this payer left over" value.
      // An explicit '0' entered by the biller is honoured as-is.
      // Typed (Step 13, S13-w): the four parts, and the total is their sum.
      patient_responsibility: typedPrTotal(r).typed
        ? typedPrTotal(r).total
        : r.patient_responsibility === ''
          ? Math.max(0, toFloat(r.allowed_amount) - toFloat(r.paid_amount))
          : toFloat(r.patient_responsibility),
      ...typedPrPayload(r),
      adjustment_codes:       r.adjustment_codes || null,
      denial_reason:          r.denial_reason    || null,
      notes:                  r.notes            || null,
    })),
  });

  const validate = () => {
    if (!header.payer_name.trim()) return 'Payer name is required.';
    if (!header.check_number.trim()) return 'Check / EFT # is required.';
    if (!header.remit_date) return 'Remit date is required.';
    if (claimRows.length === 0) return 'Add at least one claim.';
    return null;
  };

  const handleSaveDraft = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true);
    setError(null);
    try {
      if (batchId) {
        await updateRemitBatch(batchId, buildPayload('on_hold'));
      } else {
        await createRemitBatch(buildPayload('on_hold'));
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to save remit batch.');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    if (!isBalanced) { setError('Total paid must equal the check amount before finalizing.'); return; }
    setSaving(true);
    setError(null);
    try {
      if (batchId) {
        await updateRemitBatch(batchId, buildPayload('on_hold'));
        await finalizeRemitBatch(batchId);
      } else {
        await createRemitBatch(buildPayload('finalized'));
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to finalize remit batch.');
    } finally {
      setSaving(false);
    }
  };

  const handleOverride = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    if (!isBalanced) { setError('Total paid must equal the check amount before saving an override.'); return; }
    if (!reason.trim()) { setError('A reason is required to override a finalized remit.'); return; }
    setSaving(true);
    setError(null);
    try {
      await overrideRemitBatch(batchId, { ...buildPayload('finalized'), reason: reason.trim() });
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to override remit batch.');
    } finally {
      setSaving(false);
    }
  };

  const isFinalized = status === 'finalized' || (readonly && batchId);

  const title = overriding
    ? 'Override Remit'
    : readonly
      ? 'View Remit'
      : batchId
        ? 'Resume Remit'
        : mode === 'single' ? 'Record Single Remit' : 'Record Batch Remit';

  return (
    <div className="brm-overlay">
      <div className="brm-modal" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="brm-header">
          <div className="brm-header-left">
            <h2 className="brm-title">{title}</h2>
            <p className="brm-subtitle">
              {overriding ? 'Correcting a finalized remit — a reason is required' : readonly ? 'Read-only view of a finalized remit' : step === 'mode' ? 'Select remit type' : `${header.payer_name || 'No payer selected'}`}
            </p>
          </div>
          <button className="brm-close" onClick={onClose}><CloseIcon /></button>
        </div>

        {/* Body */}
        <div className="brm-body">
          {loadingBatch ? (
            <div className="brm-loading"><div className="brm-spinner" /><span>Loading…</span></div>
          ) : step === 'mode' ? (
            /* ── Mode selection ── */
            <div className="brm-mode-select">
              <button className="brm-mode-card" onClick={() => { setMode('single'); setStep('entry'); }}>
                <div className="brm-mode-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                </div>
                <span className="brm-mode-label">Single Claim Remit</span>
                <span className="brm-mode-desc">Record payment for one specific claim</span>
              </button>
              <button className="brm-mode-card" onClick={() => { setMode('batch'); setStep('entry'); }}>
                <div className="brm-mode-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                  </svg>
                </div>
                <span className="brm-mode-label">Batch Remit</span>
                <span className="brm-mode-desc">Record one check covering multiple claims from one payer</span>
              </button>
            </div>
          ) : (
            /* ── Entry form ── */
            <>
              {/* Prior overrides on this finalized batch */}
              {overrides.length > 0 && <OverrideBanner overrides={overrides} />}

              {/* Batch Header */}
              <div className="brm-section">
                <h4 className="brm-section-title">Remittance Header</h4>
                <div className="brm-grid-2">
                  <label className="brm-field">
                    <span>Payer</span>
                    <PayerCombobox
                      value={header.payer_name}
                      payerId={header.payer_id}
                      disabled={readonly}
                      onChange={({ payer_id, payer_name }) =>
                        setHeader((h) => ({ ...h, payer_id, payer_name }))
                      }
                    />
                  </label>
                  <label className="brm-field">
                    <span>Check / EFT #</span>
                    <input className="brm-input" type="text" value={header.check_number} onChange={setHead('check_number')} placeholder="Check or EFT number" disabled={fieldsDisabled} />
                  </label>
                  <label className="brm-field">
                    <span>Check Amount</span>
                    <input className="brm-input" type="number" step="0.01" min="0" value={header.check_amount} onChange={setHead('check_amount')} placeholder="Total on the check" disabled={fieldsDisabled} />
                  </label>
                  <label className="brm-field">
                    <span>Remit Date</span>
                    <input className="brm-input" type="date" value={header.remit_date} onChange={setHead('remit_date')} disabled={fieldsDisabled} />
                  </label>
                  <label className="brm-field">
                    <span>Date Submitted <span className="brm-optional">(optional)</span></span>
                    <input className="brm-input" type="date" value={header.submitted_date} onChange={setHead('submitted_date')} disabled={fieldsDisabled} />
                  </label>
                </div>
              </div>

              {/* Claim rows */}
              <div className="brm-section">
                <div className="brm-section-header-row">
                  <h4 className="brm-section-title">Claims</h4>
                  <span className="brm-claim-count">{claimRows.length} claim{claimRows.length !== 1 ? 's' : ''}</span>
                </div>

                {claimRows.length === 0 ? (
                  <div className="brm-no-claims">
                    {readonly ? 'No claims recorded.' : 'Search for a patient below to add claims to this remit.'}
                  </div>
                ) : (
                  <div className="brm-claim-list">
                    {claimRows.map((row) => (
                      <ClaimRow
                        key={row.claim_id}
                        row={row}
                        onChange={updateRow}
                        onRemove={removeRow}
                        readonly={readonly}
                        fieldsDisabled={fieldsDisabled}
                        sharedRemitDate={header.remit_date}
                      />
                    ))}
                  </div>
                )}

                {/* Patient search — only in edit mode */}
                {!readonly && (
                  <div className="brm-patient-search-section">
                    <p className="brm-patient-search-label">Add a claim</p>
                    <PatientSearch
                      payerId={header.payer_id}
                      onAddClaim={addClaim}
                      existingClaimIds={claimRows.map((r) => r.claim_id)}
                      disabled={!header.payer_id}
                    />
                    {!header.payer_id && (
                      <p className="brm-search-hint">Select a payer above to enable patient search.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Override reason — required to save a correction on a finalized batch */}
              {overriding && (
                <div className="brm-section">
                  <label className="brm-field">
                    <span>Reason for override <span className="brm-required">*</span></span>
                    <textarea
                      className="brm-input brm-textarea"
                      rows={2}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g. Transposed digits while keying the paid amount"
                      autoFocus
                    />
                  </label>
                </div>
              )}

              {/* Validation bar */}
              {(!readonly || overriding) && header.check_amount && (
                <div className={`brm-validation-bar ${isBalanced ? (hasDenials ? 'denied' : 'balanced') : claimRows.length > 0 ? 'unbalanced' : 'neutral'}`}>
                  <div className="brm-val-left">
                    {isBalanced && !hasDenials ? (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                    )}
                    <span>
                      {isBalanced
                        ? checkAmount === 0
                          ? zeroPayLabel
                          : 'Totals balanced — ready to finalize'
                        : claimRows.length === 0
                          ? 'Add claims to begin'
                          : deficit > 0
                            ? `${fmt(deficit)} unaccounted — add more claims or adjust paid amounts`
                            : `Over by ${fmt(Math.abs(deficit))} — check paid amounts`
                      }
                    </span>
                  </div>
                  <div className="brm-val-right">
                    <span className="brm-val-label">Total Paid</span>
                    <span className={`brm-val-amount ${!isBalanced && claimRows.length > 0 ? 'mismatch' : ''}`}>
                      {fmt(totalPaid)}
                    </span>
                    <span className="brm-val-sep">/</span>
                    <span className="brm-val-label">Check Amount</span>
                    <span className="brm-val-amount">{fmt(checkAmount)}</span>
                  </div>
                </div>
              )}

              {error && <div className="brm-error">{error}</div>}
            </>
          )}
        </div>

        {/* Footer */}
        {step !== 'mode' && (
          <div className="brm-footer">
            {overriding ? (
              <>
                <button
                  className="brm-btn-cancel"
                  onClick={() => { setOverriding(false); setReason(''); setError(null); }}
                  disabled={saving}
                >
                  Cancel Override
                </button>
                <div className="brm-footer-actions">
                  <button
                    className="brm-btn-finalize"
                    onClick={handleOverride}
                    disabled={saving || !isBalanced || !reason.trim()}
                    title={!isBalanced ? 'Total paid must match check amount' : !reason.trim() ? 'A reason is required' : undefined}
                  >
                    {saving ? 'Saving…' : 'Save Override'}
                  </button>
                </div>
              </>
            ) : readonly ? (
              <>
                <button className="brm-btn-cancel" onClick={onClose}>Close</button>
                {isFinalized && (
                  <div className="brm-footer-actions">
                    <button
                      className="brm-btn-draft"
                      onClick={() => { setOverriding(true); setError(null); }}
                      disabled={!canMove}
                      title={canMove ? 'Correct a typo on this finalized remit (records a reason)' : MONEY_ROLE_HINT}
                    >
                      Override
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <button className="brm-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
                <div className="brm-footer-actions">
                  <button
                    className="brm-btn-draft"
                    onClick={handleSaveDraft}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save as Draft'}
                  </button>
                  <button
                    className="brm-btn-finalize"
                    onClick={handleFinalize}
                    disabled={saving || !isBalanced}
                    title={!isBalanced ? 'Total paid must match check amount to finalize' : undefined}
                  >
                    {saving ? 'Saving…' : 'Finalize'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
